<template>
  <div
    v-loading.fullscreen.lock="isUploading"
    :element-loading-text="loadingText"
    class="asset-inline-upload"
  >
    <el-upload
      class="upload-area"
      drag
      :auto-upload="false"
      :show-file-list="false"
      :accept="acceptedFileTypes"
      @change="handleFileChange"
    >
      <el-icon class="el-icon--upload">
        <font-awesome-icon :icon="['fas', 'cloud-upload']" />
      </el-icon>
      <div class="el-upload__text">
        Drop file here or <em>click to upload</em>
      </div>
      <template #tip>
        <div class="el-upload__tip">Accepts: {{ acceptTypeDisplay }}</div>
      </template>
    </el-upload>
  </div>
</template>
<script>
import { UploadFilled } from '@element-plus/icons-vue';
import {
  detectAssetType,
  uploadAsset,
  getAcceptedFileTypes,
} from 'src/util/assetUtils.js';

export default {
  name: 'AssetInlineUpload',

  components: {
    UploadFilled,
  },

  props: {
    assetTypes: {
      type: Array,
      default: () => ['image'],
      validator: (value) => {
        const validTypes = [
          'image',
          'video',
          'audio',
          'pdf',
          'text',
          'binary',
          'html',
        ];
        return value.every((type) => validTypes.includes(type));
      },
    },
    imageFormats: {
      type: Array,
      default: () => [], // Empty, means accept all image formats
      validator: (value) => {
        const validFormats = ['jpeg', 'jpg', 'png', 'gif', 'webp', 'svg'];
        return value.every((format) =>
          validFormats.includes(format.toLowerCase())
        );
      },
    },
    isPublic: {
      type: Boolean,
      default: true,
    },
    isVerification: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['upload-complete', 'upload-error'],

  data() {
    return {
      isUploading: false,
      currentUploadCount: 0,
      totalUploadCount: 0,
    };
  },

  computed: {
    loadingText() {
      return `Uploading files... (${this.currentUploadCount} of ${this.totalUploadCount})`;
    },

    acceptedFileTypes() {
      const baseTypes = getAcceptedFileTypes(this.assetTypes);

      if (this.imageFormats.length > 0 && this.assetTypes.includes('image')) {
        const nonImageTypes = baseTypes
          .split(',')
          .filter((type) => !type.trim().startsWith('image/'));

        const mimeTypeSet = new Set();
        this.imageFormats.forEach((format) => {
          if (
            format.toLowerCase() === 'jpg' ||
            format.toLowerCase() === 'jpeg'
          ) {
            mimeTypeSet.add('image/jpeg');
          } else {
            mimeTypeSet.add(`image/${format.toLowerCase()}`);
          }
        });

        return [...nonImageTypes, ...mimeTypeSet].join(',');
      }

      return baseTypes;
    },

    acceptTypeDisplay() {
      if (this.imageFormats.length > 0 && this.assetTypes.includes('image')) {
        const nonImageTypes = this.assetTypes.filter(
          (type) => type !== 'image'
        );

        const uniqueFormats = [
          ...new Set(
            this.imageFormats.map((format) =>
              format.toLowerCase() === 'jpg' || format.toLowerCase() === 'jpeg'
                ? 'JPEG'
                : format.toUpperCase()
            )
          ),
        ];
        const imageFormatDisplay = uniqueFormats.join(', ');

        if (nonImageTypes.length > 0) {
          return `${imageFormatDisplay}, ${nonImageTypes.join(', ')}`;
        }
        return imageFormatDisplay;
      }

      return this.assetTypes.join(', ');
    },
  },

  methods: {
    async handleFileChange(file) {
      const fileList = [file.raw];
      let files = Array.from(fileList).filter((file) =>
        this.assetTypes.includes(detectAssetType(file))
      );

      if (this.imageFormats.length > 0 && this.assetTypes.includes('image')) {
        files = files.filter((file) => {
          if (detectAssetType(file) !== 'image') return true;

          const fileExtension = file.name.split('.').pop().toLowerCase();
          return this.imageFormats.some((format) => {
            if (
              (format === 'jpg' || format === 'jpeg') &&
              (fileExtension === 'jpg' || fileExtension === 'jpeg')
            ) {
              return true;
            }
            return fileExtension === format.toLowerCase();
          });
        });
      }

      if (!files.length) {
        const formatMessage =
          this.imageFormats.length > 0 && this.assetTypes.includes('image')
            ? `image formats (${this.imageFormats.join(', ')})`
            : this.assetTypes.join(', ');

        this.$message({
          message: `Please provide files of the following types: ${formatMessage}`,
          type: 'error',
          showClose: true,
        });
        return;
      }

      await this.uploadFiles(files);
    },

    async uploadFiles(files) {
      this.isUploading = true;
      this.totalUploadCount = files.length;
      this.currentUploadCount = 0;

      for (const file of files) {
        this.currentUploadCount++;
        // Set isPublic/isVerification property on file
        file.isPublic = this.isPublic;
        file.isVerification = this.isVerification;
        const { success, asset, error } = await uploadAsset(file);

        if (success) {
          this.$emit('upload-complete', asset);
        } else {
          this.$emit('upload-error', error);
        }
      }

      this.isUploading = false;
      this.currentUploadCount = 0;
      this.totalUploadCount = 0;
    },
  },
};
</script>

<style scoped>
.asset-inline-upload {
  width: 100%;
}

.upload-area {
  width: 100%;
}

:deep(.el-upload-dragger) {
  width: 100%;
  height: auto;
  padding: 20px;
}

.el-upload__text {
  margin: 10px 0;

  em {
    color: #409eff;
    font-style: normal;
  }
}

.el-upload__tip {
  font-size: 12px;
  color: #909399;
  margin-top: 7px;
}
</style>
